import React, { createContext, ReactElement, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Chat from '@buyside/components/presentation/ChatLayout/Chat/chat';
import ChatNavigation, {
  UseChatNavigationType
} from '@buyside/components/presentation/ChatLayout/ChatNavigation/chatNavigation';
import ErrorBoundary from '@buyside/components/presentation/ErrorBoundary/errorBoundary';

import { debounce } from '@buyside/util/lodashAlt';

import {
  Chat as ChatType,
  ChatDocument,
  ChatQuery,
  Maybe,
  useMarkChatReadMutation,
  useOnChatMessageCreatedSubscription
} from '@interfaces/graphql-typings';

import styles from './chatLayoutStyles.scss';

export const ChatContext = createContext<
  Maybe<{
    canManageMessages: boolean;
    canViewIdeas: boolean;
    currentUserId: string;
    indexElement?: ReactElement;
    useChatNavigation: () => UseChatNavigationType;
  }>
>(null);

const ChatLayout: React.FC = () => {
  const { chatId } = useParams();

  const chatContext = useContext(ChatContext);

  const [handleMarkChatRead] = useMarkChatReadMutation();

  if (!chatContext) throw new Error();

  const { canViewIdeas, indexElement } = chatContext;

  const debouncedHandleMarkChatRead = useMemo(
    () =>
      debounce(() => handleMarkChatRead({ variables: { chatId: `${chatId}` } }), 1000, {
        maxWait: 5000
      }),
    [chatId, handleMarkChatRead]
  );

  const { error: subscriptionError } = useOnChatMessageCreatedSubscription({
    onSubscriptionData: ({ subscriptionData: { data }, client: { cache } }) => {
      const { chatId: newChatMessageChatId, chatMessage: newChatMessage } =
        data?.chatMessageCreated || {};

      if (!newChatMessageChatId || !newChatMessage) return;

      const belongsToCurrentChat = chatId === newChatMessageChatId;

      if (belongsToCurrentChat) {
        debouncedHandleMarkChatRead();

        const cachedQuery = cache.readQuery<ChatQuery>({
          query: ChatDocument,
          variables: { id: chatId }
        });
        if (!cachedQuery?.chat?.chatMessages) return;

        const { chatMessages, chatMessagesCount } = cachedQuery.chat;
        cache.writeQuery<ChatQuery>({
          query: ChatDocument,
          data: {
            ...cachedQuery,
            chat: {
              ...cachedQuery.chat,
              chatMessagesCount: chatMessagesCount + 1,
              chatMessages: {
                edges: [{ node: newChatMessage }, ...(chatMessages.edges || [])],
                pageInfo: chatMessages.pageInfo
              }
            }
          },
          variables: { id: chatId }
        });
      } else {
        cache.modify<ChatType>({
          id: `Chat:${newChatMessageChatId}`,
          fields: {
            unreadMessagesCount: prevCount =>
              newChatMessageChatId === chatId ? prevCount : prevCount + 1
          }
        });
      }
    },
    skip: !canViewIdeas
  });

  if (subscriptionError) throw subscriptionError;

  useEffect(() => {
    return (): void => {
      debouncedHandleMarkChatRead.flush();
    };
  }, [chatId, debouncedHandleMarkChatRead]);

  return (
    <div className={styles.chatLayout}>
      <ChatNavigation />
      <div className={styles.chat}>
        {chatId ? (
          <ErrorBoundary pageName="chat room">
            <Chat />
          </ErrorBoundary>
        ) : (
          indexElement || (
            <p className={styles.emptyIndexText}>Select a chat room to get started!</p>
          )
        )}
      </div>
    </div>
  );
};

export default ChatLayout;
